import lighting_include from '../shader-includes/lighting.glsl?raw'
import wireframe_fragmentShader_include from '../shader-includes/wireframe_fragmentShader.glsl?raw'
import wireframe_vertexShader_include from '../shader-includes/wireframe_vertexShader.glsl?raw'
import { useResource } from './useResource.ts'

const rxInclude = /^#include\s+(\w+)\s*$/

function preprocessLine(gl: WebGL2RenderingContext, type: GLenum, line: string): string {
  const match = rxInclude.exec(line)
  if (!match) {
    return line
  }

  const name = match[1]
  switch (type) {

  case gl.VERTEX_SHADER:
    switch (name) {
    case 'wireframe': return wireframe_vertexShader_include
    default: throw new Error(`unknown vertex shader include: ${name}`)
    }

  case gl.FRAGMENT_SHADER:
    switch (name) {
    case 'lighting': return lighting_include
    case 'wireframe': return wireframe_fragmentShader_include
    default: throw new Error(`Unknown shader include: ${name}`)
    }

  default:
    throw new Error(`unknown shader type ${type}`)
  }

}

function preprocess(gl: WebGL2RenderingContext, type: GLenum, source: string): string {
  return source
    .split('\n')
    .map(line => preprocessLine(gl, type, line))
    .join('\n')
}

function compileShader(name: string, gl: WebGL2RenderingContext, type: GLenum, source: string): WebGLShader {
  const preprocessedSource = preprocess(gl, type, source)

  const shader = gl.createShader(type)
  if (!shader) {
    throw new Error(`gl.createShader(${type}) failed`)
  }
  gl.getExtension('GMAN_debug_helper')?.tagObject(shader, name)
  try {
    gl.shaderSource(shader, preprocessedSource)
    gl.compileShader(shader)
    const success = gl.getShaderParameter(shader, gl.COMPILE_STATUS)
    if (success) {
      return shader
    }
    throw new Error(`gl.compileShader(${type}) failed:\n${gl.getShaderInfoLog(shader)}`)
  } catch (e) {
    gl.deleteShader(shader)
    throw e
  }
}

function compileProgram(
  name: string,
  gl: WebGL2RenderingContext,
  vertexShader: string,
  fragmentShader: string
): WebGLProgram {
  const vShader = compileShader(`${name} vertex shader`, gl, gl.VERTEX_SHADER, vertexShader)
  try {
    const fShader = compileShader(`${name} fragment shader`, gl, gl.FRAGMENT_SHADER, fragmentShader)
    try {
      const program = gl.createProgram()
      if (!program) {
        throw new Error('gl.createProgram() failed')
      }
      gl.getExtension('GMAN_debug_helper')?.tagObject(program, name)
      try {
        gl.attachShader(program, vShader)
        gl.attachShader(program, fShader)
        gl.linkProgram(program)
        const success = gl.getProgramParameter(program, gl.LINK_STATUS)
        if (success) {
          gl.detachShader(program, vShader)
          gl.deleteShader(vShader)
          gl.detachShader(program, fShader)
          gl.deleteShader(fShader)
          return program
        }
        throw new Error(`gl.linkProgram() failed:\n${gl.getProgramInfoLog(program)}`)
      } catch (e) {
        gl.deleteProgram
        throw e
      }
    } catch (e) {
      gl.deleteShader(fShader)
      throw e
    }
  } catch (e) {
    gl.deleteShader(vShader)
    throw e
  }
}

export function useProgram(name: string, gl: WebGL2RenderingContext, vertexSource: string, fragmentSource: string): WebGLProgram {
  return useResource(function* () {
    const program = compileProgram(name, gl, vertexSource, fragmentSource)

    yield program

    gl.deleteProgram(program)
  }, [gl, vertexSource, fragmentSource])
}
